import { Button, Card, Divider, Flex, Typography } from "antd";
import PlanLabel from "../subscriptions/PlanLabel";
import CustomIcon from "../layouts/CustomIcon";
import PooledCreditUsageTooltip from "../recruit/layouts/PooledCreditUsageTooltip";
import PooledCreditUsageProgressBar from "../recruit/layouts/PooledCreditUsageProgressBar";
import CreditsHelpModal from "../recruit/layouts/CreditsHelpModal";
import { useState } from "react";
import { getFormattedDayjsDate, normalizeCreditsValue, roundCredits } from "../../utills/common";
import { useAuthContext } from "../user/AuthProvider";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../../utills/paths";

const { Text } = Typography;

export default function CreditsModal({ individualDashboard = false }) {
  const [showCreditsHelpModal, setShowCreditsHelpModal] = useState(false);
  const [showPooledCreditUsageTooltip, setShowPooledCreditUsageTooltip] = useState(false);

  const navigate = useNavigate();
  const { planInfo, subscription, organization, creditPrices, userAccess, totalCreditsUsed, usedPercentage } = useAuthContext();
  const { isOrgAdminOrOwner = false } = userAccess || {};
  const { hasResponseSubscription = false } = subscription || {};
  const { own_users, plan_comparison } = subscription?.plan || {};
  const { credits_response_total, workspaces, credits_response_permanent } = organization || {};
  const { isCustomPlan = false, isPlanCancelled = false, balances = {}, isTrialPlan = false, upcomingInvoice = null } = planInfo || {};
  const { credits: { used: { stripeMeter } = {} } = {} } = balances || {};
  const platformRate = Number(creditPrices?.response?.amount) || 0;
  const multipleWorkspaces = workspaces?.length > 1;

  return (
    <>
      <Card
        hoverable={false}
        variant={!individualDashboard ? "outlined" : "borderless"}
        className={`shadow-none m-2 ${!individualDashboard ? "bg-F5F6F9" : "bg-white"} ${!individualDashboard ? "border-DAE3FB" : ""}`}
        styles={{ body: { padding: !individualDashboard ? "5px 10px" : 0 } }}
      >
        <Flex vertical gap={5}>
          {individualDashboard && (
            <>
              <Flex align='center' justify='space-between'>
                <Flex align='center'>
                  <PlanLabel noColor={true} />
                  {!isCustomPlan && isOrgAdminOrOwner && !isPlanCancelled && (
                    <Button
                      size='small'
                      type='text'
                      className='text-underline font-size-12px color-2E62E9 fw-500'
                      onClick={() => navigate(PATHS.V2.PLANS)}
                    >
                      Upgrade
                    </Button>
                  )}
                </Flex>
                <Button
                  size='small'
                  type='text'
                  className='margin-right-n10px'
                  icon={<CustomIcon name='help-circle' width={14} height={14} />}
                  onClick={() => setShowCreditsHelpModal(true)}
                />
              </Flex>

              <Divider className='m-0 border-DAE3FB' />
            </>
          )}

          <Flex vertical>
            <Flex align='center' justify='space-between'>
              <Flex align='center' gap={1}>
                <Text className='font-size-12px fw-600 color-3F3F50'>Pooled Credit Usage</Text>
                <PooledCreditUsageTooltip
                  open={showPooledCreditUsageTooltip}
                  setOpen={setShowPooledCreditUsageTooltip}
                  onOpenCreditsHelp={() => setShowCreditsHelpModal(true)}
                >
                  <Button size='small' type='text' className='p-0 m-0' icon={<CustomIcon name='base_tooltip' />} />
                </PooledCreditUsageTooltip>
              </Flex>
              {usedPercentage >= 0 && (
                <Text className='font-size-12px fw-600'>
                  {!multipleWorkspaces ? `${usedPercentage}% used` : `${totalCreditsUsed} used`}
                </Text>
              )}
            </Flex>

            <PooledCreditUsageProgressBar individualDashboard={individualDashboard} multipleWorkspaces={multipleWorkspaces} />
          </Flex>

          <Flex align='center' justify='space-between'>
            {!isCustomPlan && !isTrialPlan && (
              <Text className='font-size-12px color-3F3F50'>
                {`${roundCredits(credits_response_total)}/${roundCredits(own_users + credits_response_permanent)} Credits remaining`}
              </Text>
            )}
            {(isCustomPlan || isTrialPlan) && (
              <Text className='font-size-12px color-3F3F50'>
                {multipleWorkspaces
                  ? `${roundCredits(credits_response_total)} Credits remaining`
                  : `${roundCredits(credits_response_total)}/${roundCredits(
                      credits_response_total + totalCreditsUsed
                    )} Credits remaining`}
              </Text>
            )}

            {Number(stripeMeter) > 0 && (
              <Text className='font-size-12px color-D50B3E fw-600'>{roundCredits(stripeMeter)} over limit</Text>
            )}
          </Flex>

          {usedPercentage >= 60 && hasResponseSubscription && (
            <Card
              className={`bg-FFF9EB border-FFDA85 color-8A6100 w-100 bl-FFC233-2px mb-1`}
              styles={{ body: { padding: "8px" } }}
              style={{ borderRadius: "0 8px 8px 0" }}
            >
              <Flex gap={10} align='flex-start'>
                <CustomIcon name='alert_circle' width={24} height={24} />
                <Flex gap={5} vertical>
                  <Text className='color-8A6100 fw-600 font-size-12px'>
                    {usedPercentage >= 100
                      ? Number(stripeMeter) > 0
                        ? "You've exceeded your allocated pooled credits"
                        : "You've used all your allocated pooled credits"
                      : "You've used most of your pooled credits"}
                  </Text>
                  <Flex vertical gap={10}>
                    <Text className='color-8A6100 font-size-12px'>
                      {Number(stripeMeter) > 0
                        ? `Auto-billing is active. Overages (${roundCredits(stripeMeter)} credits${
                            isOrgAdminOrOwner ? `, $${roundCredits(Number(stripeMeter) * platformRate)}` : ""
                          }) will be charged to your saved payment method on your next invoice (${getFormattedDayjsDate(
                            upcomingInvoice?.period_end ? upcomingInvoice?.period_end * 1000 : subscription?.end_date
                          )})`
                        : "Auto-billing is active. Overages will be charged to your saved payment method on your next invoice."}
                    </Text>
                    {isOrgAdminOrOwner && (
                      <Button
                        size='small'
                        className='color-8A6100 border-8A6100 p-8-12'
                        onClick={() => navigate(PATHS.V2.CREDITSUSAGE)}
                      >
                        See Usage Details
                      </Button>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </Card>
          )}

          {!isCustomPlan && !isTrialPlan && individualDashboard && (
            <Card className='bg-white shadow-none' variant='borderless' hoverable={false} styles={{ body: { padding: "5px" } }}>
              <Flex vertical gap={10}>
                <Text className='fw-600 font-size-12px'>Monthly Response Allocation</Text>

                <ul className='m-0' style={{ padding: "0 15px" }}>
                  <li>
                    <Text className='font-size-12px'>
                      {normalizeCreditsValue(plan_comparison?.task)} Unmoderated Test responses (With recording), OR
                    </Text>
                  </li>
                  <li>
                    <Text className='font-size-12px'>
                      {normalizeCreditsValue(plan_comparison?.noRecording)} Unmoderated Test responses (No recording), OR
                    </Text>
                  </li>
                  <li>
                    <Text className='font-size-12px'>{normalizeCreditsValue(plan_comparison?.moderated)} User Interviews, OR</Text>
                  </li>
                  <li>
                    <Text className='font-size-12px'>{normalizeCreditsValue(plan_comparison?.survey)} Survey responses, OR</Text>
                  </li>
                  <li>
                    <Text className='font-size-12px'>
                      {normalizeCreditsValue(plan_comparison?.cardsort)} Card Sorting responses, OR
                    </Text>
                  </li>
                  <li>
                    <Text className='font-size-12px'>
                      {normalizeCreditsValue(plan_comparison?.treetest)} Tree Testing responses, OR
                    </Text>
                  </li>
                  <li>
                    <Text className='font-size-12px'>Any combination within the credit limit</Text>
                  </li>
                </ul>
              </Flex>
            </Card>
          )}
        </Flex>
      </Card>

      <CreditsHelpModal open={showCreditsHelpModal} setOpen={setShowCreditsHelpModal} />
    </>
  );
}
